import { useMutation, useQuery } from '@tanstack/react-query';
import {
  sinoClubInstance,
  webgwjsInstance,
  webgwInstance,
} from '../redux/request/request';
import { getAccessToken } from "../helpers/authHelper";

type CheckEmailResponse = Partial<{
  accountId: number
  upnEmail: string
  accountEnabled: boolean
  authMode: string
  superPlusAdmin: boolean
  authenticationPath: string
  redirectUrl: string
}>;

type CheckEmailVariables = {
  upnEmail: string
};

const useCheckEmail = (options = {}) => useMutation({
  mutationFn: async ({ upnEmail }: CheckEmailVariables) => {
    console.log(upnEmail)
    const response = await webgwjsInstance.post<CheckEmailResponse>(
      '/api/v1/user/login/email-pre-check',
      { upnEmail },
    );

    return response.data;
  },
  ...options,
});

export type UpdateMerchantMemberReferralVariables = {
  merchantId: string | number
  allowMemberReferral: boolean
};

const useUpdateMerchantMemberReferral = (options = {}) => useMutation({
  mutationFn: async (data: UpdateMerchantMemberReferralVariables) => {
    const response = await webgwInstance.put(
      '/api/v1/merchant/member-referral/allow',
      data,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      },
    );

    return response.data;
  },
  ...options,
});

export type UpdateShopAcceptSCouponSDollarVariables = {
  shopId: string | number;
  participatesInSDollar: boolean;
};

const useUpdateShopAcceptSCouponSDollar = (options = {}) => useMutation({
  mutationFn: async (data: UpdateShopAcceptSCouponSDollarVariables) => {
    const response = await webgwInstance.put(
      '/api/v1/mall/shop/s-dollar/participation ',
      data,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      },
    );

    return response.data;
  },
  ...options,
});

export type ResumeSpendingTransactionVariables = unknown;

const useResumeSpendingTransaction = (options = {}) => useMutation({
  mutationFn: async (data: ResumeSpendingTransactionVariables) => {
    const response = await webgwInstance.put(
      '/api/v1/spending/resume',
      data,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      },
    );

    return response.data;
  },
  ...options,
});

export type SubmitOver14DaysAgoSpendingTransactionVariables = unknown;

const useSubmitOver14DaysAgoSpendingTransaction = (options = {}) => useMutation({
  mutationFn: async (data: SubmitOver14DaysAgoSpendingTransactionVariables) => {
    const response = await webgwInstance.post(
      '/api/v1/spending/submit/old-v2',
      data,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      },
    );

    return response.data;
  },
  ...options,
});

export type SubmitSpendingTransactionVariables = unknown;

const useSubmitSpendingTransaction = (options = {}) => useMutation({
  mutationFn: async (data: SubmitSpendingTransactionVariables) => {
    const response = await webgwInstance.post(
      '/api/v1/spending/submit/v2',
      data,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      },
    );

    return response.data;
  },
  ...options,
});

const useSinoClubMembershipUpgradeList = (
  variables: unknown,
  options = {},
) => useQuery({
  queryKey: ['sino-club-membership-upgrade-forms', variables],
  queryFn: async () => {
    const response = await sinoClubInstance.get(
      '/cms/membership-upgrade/forms',
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        params: variables,
      },
    );

    return response.data;
  },
  ...options,
});

const useSinoClubMembershipUpgradeDocumentList = (
  variables: unknown,
  options = {},
) => useQuery({
  queryKey: ['sino-club-member-upgrade-documents', variables],
  queryFn: async () => {
    const response = await sinoClubInstance.get(
      '/cms/membership-upgrade/documents',
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        params: variables,
      },
    );

    return response.data;
  },
  ...options,
});

const useUpdateSinoClubMembershipUpgradeDocument = (options = {}) => useMutation({
  mutationFn: async (data: unknown) => {
    const response = await sinoClubInstance.post('/cms/membership-upgrade/documents/update', data, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });

    return response.data;
  },
  ...options,
});

const useDeleteSinoClubMembershipUpgradeDocument = (options = {}) => useMutation({
  mutationFn: async (data: unknown) => {
    const response = await sinoClubInstance.post('/cms/membership-upgrade/documents/delete', data, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });

    return response.data;
  },
  ...options,
});

const useApproveSinoClubMembershipUpgradeForm = (options = {}) => useMutation({
  mutationFn: async (data: unknown) => {
    const response = await sinoClubInstance.post('/cms/membership-upgrade/forms/approve', data, {
      headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
    });

    return response.data;
  },
  ...options,
});

const useSinoClubParkingDocumentList = (
  variables: unknown,
  options = {},
) => useQuery({
  queryKey: ['sino-club-parking-documents', variables],
  queryFn: async () => {
    const response = await sinoClubInstance.get(
      '/getUpgradeFormDocumentForCMS', // Yes this path is right. It's a holdover.
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        params: variables,
      },
    );

    return response.data;
  },
  ...options,
});

const useUpdateSinoClubParkingDocument = (options = {}) => useMutation({
  mutationFn: async (data: unknown) => {
    const response = await sinoClubInstance.post(
      '/updateDocumentForCMS', // Yes this path is right. It's a holdover.
      data,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );

    return response.data;
  },
  ...options,
});

const useDeleteSinoClubParkingDocument = (options = {}) => useMutation({
  mutationFn: async (data: unknown) => {
    const response = await sinoClubInstance.post(
      '/deleteDocumentForCMS', // Yes this path is right. It's a holdover.
      data,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );

    return response.data;
  },
  ...options,
});

const useApproveSinoClubParkingForm = (options = {}) => useMutation({
  mutationFn: async (data: unknown) => {
    const response = await sinoClubInstance.post(
      '/approveForm',
      data,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );

    return response.data;
  },
  ...options,
});

export type PermissionData = {
  roleCode?: string
  sections?: {
    section?: string
    functions?: {
      function?: string,
      permissionCode?: string,
    }[]
  }[]
}

type PermissionsResponse = {
  data?: PermissionData
}

const usePermissions = (options = {}) => useQuery({
  queryKey: ['permissions'],
  queryFn: async () => {
    const response = await webgwjsInstance.get<PermissionsResponse>(
      '/api/v1/cms/me/permissions',
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
    return response.data;
  },
  ...options,
});



export {
  useCheckEmail,
  useApproveSinoClubMembershipUpgradeForm,
  useDeleteSinoClubMembershipUpgradeDocument,
  usePermissions,
  useResumeSpendingTransaction,
  useSinoClubMembershipUpgradeList,
  useSinoClubMembershipUpgradeDocumentList,
  useSinoClubParkingDocumentList,
  useSubmitSpendingTransaction,
  useSubmitOver14DaysAgoSpendingTransaction,
  useUpdateSinoClubMembershipUpgradeDocument,
  useUpdateSinoClubParkingDocument,
  useUpdateMerchantMemberReferral,
  useUpdateShopAcceptSCouponSDollar,
  useApproveSinoClubParkingForm,
  useDeleteSinoClubParkingDocument,
};
